import { useState } from 'react';
import { Fab, IconButton, Paper } from '@mui/material';
import Icon from '@mui/material/Icon';
import PropTypes from 'prop-types';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip from '@mui/material/Tooltip';
import { useIntl } from 'react-intl';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

export function GridCard({
  item,
  defaultIcon,
  onUpdateBtnClick,
  onDeleteBtnClick,
  onSelectBtnClick,
  onPlayBtnClick,
  canEdit = true
}) {
  const { formatMessage: msg } = useIntl();

  const [elevation, setElevation] = useState(1);

  return (
    <Paper
      id="GridContainer"
      elevation={elevation}
      onMouseEnter={() => setElevation(4)}
      onMouseLeave={() => setElevation(1)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '330px',
        width: '270px',
        alignItems: 'center',
        borderRadius: 2,
        transition: 'box-shadow 0.3s ease-in-out',
        background: (theme) => theme.palette.white.main,
        overflow: 'hidden'
      }}
    >
      <MDBox
        sx={{
          display: 'flex',
          alignItems: 'left',
          flexDirection: 'column',
          justifyContent: 'left',
          width: '100%',
          height: '35%',
          padding: '15px',
          position: 'relative',
          background: (theme) => {
            const mainColor = `${item.color}dd`;
            const lighterColor = `${item.color}66`;

            return theme.functions.linearGradient(lighterColor, mainColor, 150);
          }
        }}
      >
        <MDBox
          sx={{
            display: 'flex'
          }}
        >
          <MDBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 50,
              height: 50,
              borderRadius: '50%',
              backgroundColor: (theme) => theme.palette.white.main,
              boxShadow: (theme) => theme.shadows[2]
            }}
          >
            <Icon
              fontSize="medium"
              sx={{
                color: (theme) => theme.palette.primary.main
              }}
            >
              {item.icon ?? defaultIcon}
            </Icon>
          </MDBox>

          {canEdit && (
            <MDBox
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                display: 'flex',
                gap: 1
              }}
            >
              <Tooltip title={msg({ id: 'tooltip.edit' })}>
                <IconButton
                  size="small"
                  aria-label="Edit"
                  onClick={() => onUpdateBtnClick()}
                  sx={{
                    color: (theme) => theme.palette.primary.main,
                    backgroundColor: (theme) => theme.palette.white.main
                  }}
                >
                  <EditNoteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={msg({ id: 'tooltip.delete' })}>
                <IconButton
                  size="small"
                  aria-label="delete"
                  onClick={() => onDeleteBtnClick()}
                  sx={{
                    color: (theme) => theme.palette.white.main,
                    backgroundColor: (theme) => theme.palette.error.main
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </MDBox>
          )}
        </MDBox>
        <MDTypography
          variant="h5"
          sx={{
            color: (theme) => theme.palette.white.main,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%'
          }}
          mt={1.5}
        >
          {item.label}
        </MDTypography>
      </MDBox>
      <MDTypography
        variant="button"
        onClick={() => onSelectBtnClick()}
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          height: '45%'
        }}
      >
        {item.description}
      </MDTypography>
      {onPlayBtnClick ? (
        <MDBox sx={{ display: 'flex', p: 1, width: '100%', justifyContent: 'end' }}>
          <Tooltip title={msg({ id: 'tooltip.play' })}>
            <Fab size="small" color="primary" aria-label="add" onClick={onPlayBtnClick}>
              <PlayArrowRoundedIcon fontSize="medium" />
            </Fab>
          </Tooltip>
        </MDBox>
      ) : null}
    </Paper>
  );
}

GridCard.propTypes = {
  item: PropTypes.shape({
    icon: PropTypes.string,
    label: PropTypes.string.isRequired,
    description: PropTypes.string,
    color: PropTypes.string
  }).isRequired,
  defaultIcon: PropTypes.string,
  onUpdateBtnClick: PropTypes.func.isRequired,
  onDeleteBtnClick: PropTypes.func.isRequired,
  onSelectBtnClick: PropTypes.func.isRequired,
  onPlayBtnClick: PropTypes.func,
  canEdit: PropTypes.bool
};
